import { useEffect, useRef } from 'react'
import { useHeardPlaid } from '@/components/plaid/helpers'
import {
  selectFinancialAccountById,
  selectPlaidTarget,
} from '@/selectors/financeSelectors'
import {
  clearPlaidTarget,
  DEFAULT_PLAID_SURFACE,
  type PlaidLauncherSurface,
  type PlaidLauncherTarget,
} from '@/reducers/finances/plaidLauncherReducer'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

const PlaidLauncherActive = ({ target }: { target: PlaidLauncherTarget }) => {
  const dispatch = useAppDispatch()
  const openedRef = useRef(false)

  // Only `connect` has no account yet — an id here is what makes the token update mode.
  const accountId = target.mode !== 'connect' ? target.accountId : undefined
  const account = useReselector(selectFinancialAccountById, accountId)

  const { open, ready } = useHeardPlaid({
    // `connect` = brand-new institution (no account → new link token);
    // `reconnect` / `manage` = existing account (account → update link token).
    // Only `reconnect` is `disconnected`, which makes the returned account list
    // non-authoritative; `manage` needs it authoritative so removals take effect.
    account: accountId ? account : null,
    disconnected: target.mode === 'reconnect',
    redirectLink: '',
    shouldRedirectOnExit: false,
    onClose: () => dispatch(clearPlaidTarget()),
  })

  useEffect(() => {
    // For the account-scoped modes we must wait until the account resolves from the store.
    const targetResolved = target.mode === 'connect' || Boolean(account)
    if (targetResolved && ready && !openedRef.current) {
      openedRef.current = true
      open()
    }
  }, [target.mode, account, ready, open])

  return null
}

/**
 * Single Plaid entry point for one surface. It stays inert (no hook, no
 * link-token fetch) until a card, modal, or the connect menu publishes a target
 * for that surface via `setPlaidTarget`; only then does it mount
 * `useHeardPlaid`, fetch the appropriate link token once, and open Plaid. On close
 * it clears the target and unmounts. This keeps the page at zero Plaid fetches on
 * load regardless of how many accounts are rendered, and gives every
 * connect/reconnect affordance on that surface one shared Plaid instance.
 */
export const PlaidLauncher = ({
  surface = DEFAULT_PLAID_SURFACE,
}: {
  surface?: PlaidLauncherSurface
} = {}) => {
  const target = useReselector(selectPlaidTarget)
  if (!target) return null
  if ((target.surface ?? DEFAULT_PLAID_SURFACE) !== surface) return null
  return <PlaidLauncherActive target={target} />
}
